import { httpClient } from './core';

// Dashboard + People helper
// Der Server liefert nur die Kennzahlen, die der Benutzer sehen darf (Entscheidung F31).
export interface DashboardKpis {
  activeStudents?: number;
  activeTeachers?: number;
  upcomingTrials?: number;
  openTasks?: number;
  openApplications?: number;
}

export interface AdminDashboardKpis {
  activeStudents: number;
  activeTeachers: number;
  trialRequests: number;
  openTasks: number;
  bookedRevenueCents: number;
}

export interface AdminDashboardLocationItem {
  id: number;
  name: string;
  studentsCount: number;
  teachersCount: number;
  occupancyRate: number;
}

export interface AdminDashboardRevenuePoint {
  monthKey: string;
  label: string;
  bookedRevenueCents: number;
}

export interface AdminDashboardOccupancyPoint {
  weekday: number;
  label: string;
  activeParticipants: number;
  occupancyRate: number;
}

export interface AdminDashboardOverview {
  kpis: AdminDashboardKpis;
  locations: AdminDashboardLocationItem[];
  charts: {
    revenueByMonth: AdminDashboardRevenuePoint[];
    occupancyByWeekday: AdminDashboardOccupancyPoint[];
  };
}

export type AdminReportPeriodType = 'quarter' | 'year';
export type AdminReportFormat = 'pdf' | 'csv';

export interface AdminReportKpis {
  activeStudents: number;
  activeTeachers: number;
  trialRequests: number;
  cancellations: number;
  newEnrollments: number;
  /** Forderungen abzüglich Gutschriften und Stornos (T-0197). */
  bookedRevenueCents: number;
  /** Zahlungen abzüglich Rücklastschriften und Erstattungen. */
  receivedRevenueCents: number;
}

export interface AdminReportLocationRow {
  standortId: number;
  standortName: string;
  activeStudents: number;
  newEnrollments: number;
  trialRequests: number;
  cancellations: number;
  bookedRevenueCents: number;
  receivedRevenueCents: number;
}

export interface AdminReportTeacherRow {
  teacherId: number;
  teacherName: string;
  activeStudents: number;
  newEnrollments: number;
  cancellations: number;
  /** Anfragen, aus denen eine Probestunde bei der Lehrkraft wurde. */
  trialRequests: number;
  bookedRevenueCents: number;
  receivedRevenueCents: number;
}

export interface AdminReportSubjectRow {
  subjectId: number;
  subjectName: string;
  activeCourses: number;
  activeStudents: number;
  newEnrollments: number;
}

export interface AdminReportSummary {
  reportId: string;
  periodType: AdminReportPeriodType;
  period: string;
  periodLabel: string;
  range: {
    startDate: string;
    endDate: string;
  };
  kpis: AdminReportKpis;
  details: {
    byLocation: AdminReportLocationRow[];
    byTeacher: AdminReportTeacherRow[];
    bySubject: AdminReportSubjectRow[];
  };
}

export interface StandortKpiItem {
  standortId: number;
  standortName: string;
  openOwnTasks: number;
  upcomingTrials: number;
  trialRequests: number;
}

export interface RecentActivityItem {
  id: number;
  wpUserId?: number | null;
  subIndex?: number | null;
  type: string;
  description: string;
  /** Wer es getan hat und bei welcher Person (T-0132). */
  actorName?: string | null;
  personId?: number | null;
  personName?: string | null;
  createdAt: string;
}

export const dashboardApi = {
  getKpis: async (): Promise<DashboardKpis> => {
    const res = await httpClient<DashboardKpis>('/api/dashboard/kpis');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der KPIs');
    return res.data;
  },
  getKpisByStandort: async (): Promise<Record<string, StandortKpiItem>> => {
    const res = await httpClient<Record<string, StandortKpiItem>>('/api/dashboard/kpis-by-standort');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Standort-KPIs');
    return res.data;
  },
  getRecentActivities: async (limit = 20, standortId?: number): Promise<RecentActivityItem[]> => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (standortId !== undefined) params.set('standort_id', String(standortId));
    const res = await httpClient<RecentActivityItem[]>(`/api/person-history/recent?${params.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Aktivitäten');
    return res.data;
  },
};

export const adminReportsApi = {
  getSummary: async (periodType: AdminReportPeriodType, period: string): Promise<AdminReportSummary> => {
    const params = new URLSearchParams();
    params.set('periodType', periodType);
    params.set('period', period);
    const res = await httpClient<AdminReportSummary>(`/api/admin/reports/summary?${params.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden des Berichts');
    return res.data;
  },
  getDownloadUrl: async (reportId: string, format: AdminReportFormat): Promise<string> => {
    const params = new URLSearchParams();
    params.set('format', format);
    const res = await httpClient<{ url: string }>(`/api/admin/reports/${encodeURIComponent(reportId)}/download-url?${params.toString()}`);
    if (res.error || !res.data?.url) throw new Error(res.error || 'Fehler beim Erstellen des Download-Links');
    return res.data.url;
  },
};

export interface TrialRequestReportDay {
  date: string;
  total: number;
  byStandort: Record<string, number>;
}

export interface TrialRequestReportStandortRow {
  standortId: number;
  standortName: string;
  total: number;
}

export interface TrialRequestReportChannelRow {
  channel: string;
  label: string;
  paid: boolean;
  total: number;
}

export interface TrialRequestReportCampaignRow {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  total: number;
  /** Trichter je Kampagne bis „Bezahlt“ (T-0216). */
  trials?: number;
  enrollments?: number;
  paidCount?: number;
  revenueCents?: number;
}

export interface TrialRequestReportStatusRow {
  status: string;
  total: number;
}

export interface TrialRequestFunnelStage {
  key: 'requests' | 'trials' | 'enrollments' | 'paid';
  label: string;
  total: number;
}

export interface TrialRequestFunnelChannelRow {
  channel: string;
  label: string;
  paid: boolean;
  requests: number;
  trials: number;
  enrollments: number;
  paidCount: number;
  revenueCents: number;
}

export interface TrialRequestFunnel {
  stages: TrialRequestFunnelStage[];
  byChannel: TrialRequestFunnelChannelRow[];
  revenueCents: number;
}

export interface TrialRequestReport {
  range: {
    startDate: string;
    endDate: string;
    days: number;
    timezone: string;
  };
  filter: {
    standortId: number | null;
    channel?: string | null;
  };
  /** Alle Herkunftskanäle für die Auswahl, auch die ohne Anfragen (T-0159). */
  channels?: Array<{ channel: string; label: string; paid: boolean }>;
  /** Gleich lange Vorperiode mit denselben Filtern. */
  previousDays?: Array<{ date: string; total: number }>;
  previousTotal?: number;
  byFach?: Array<{ fachId: number | null; fachName: string; total: number }>;
  totals: {
    total: number;
    averagePerDay: number;
    peakDate: string | null;
    peakCount: number;
    attributed: number;
    unattributed: number;
    paid: number;
  };
  days: TrialRequestReportDay[];
  funnel: TrialRequestFunnel;
  standorte: Array<{ standortId: number; standortName: string }>;
  byStandort: TrialRequestReportStandortRow[];
  byChannel: TrialRequestReportChannelRow[];
  byCampaign: TrialRequestReportCampaignRow[];
  byStatus: TrialRequestReportStatusRow[];
}

export const trialRequestReportApi = {
  get: async (
    from: string,
    to: string,
    standortId?: number | null,
    channel?: string | null,
  ): Promise<TrialRequestReport> => {
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    if (standortId) params.set('standort_id', String(standortId));
    if (channel) params.set('channel', channel);
    const res = await httpClient<TrialRequestReport>(`/api/admin/reports/trial-requests?${params.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden des Berichts');
    return res.data;
  },
};

export const adminDashboardApi = {
  getOverview: async (): Promise<AdminDashboardOverview> => {
    const res = await httpClient<AdminDashboardOverview>('/api/admin/dashboard/overview');
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Dashboard-Daten');
    return res.data;
  },
};

export interface BirthdayPerson {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  type: 'teacher' | 'customer' | string;
  birthDate?: string | null;
  nextBirthday?: string;
}

export const peopleApi = {
  getBirthdays: async (from: string, to: string, type?: 'teacher' | 'customer', standortId?: number): Promise<BirthdayPerson[]> => {
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    if (type) params.set('type', type);
    if (standortId !== undefined) params.set('standort_id', String(standortId));
    const res = await httpClient<BirthdayPerson[]>(`/api/personen/birthdays?${params.toString()}`);
    if (res.error || !res.data) throw new Error(res.error || 'Fehler beim Laden der Geburtstage');
    return res.data;
  },
};

// Trial Requests
export interface TrialRequestItem {
  id: number;
  status?: string;
  createdAt?: string;
  name?: string;
  contactEmail?: string;
}

export const trialRequestsApi = {
  list: async (filters?: { standort_id?: number | null }): Promise<TrialRequestItem[]> => {
    const qs = filters?.standort_id ? `?standort_id=${encodeURIComponent(String(filters.standort_id))}` : '';
    const res = await httpClient<TrialRequestItem[]>(`/api/trial-requests${qs}`);
    if (res.error || !res.data) return [];
    return res.data;
  },
};
